import axios from 'axios';
import toast from 'react-hot-toast';

// In production the frontend (expertatdoor.com) and the API
// (api.expertatdoor.com) are served from different hosts, so a relative
// '/api' path would hit the static frontend itself. REACT_APP_API_URL
// (baked in via .env.production) is the primary source of truth; this
// fallback only kicks in if that env var is missing for some reason.
const PROD_API_URL = 'https://api.expertatdoor.com/api';
const isProdHost = typeof window !== 'undefined'
  && /(^|\.)expertatdoor\.com$/.test(window.location.hostname)
  && window.location.hostname !== 'api.expertatdoor.com';

const API_URL = process.env.REACT_APP_API_URL || (isProdHost ? PROD_API_URL : '/api');

const api = axios.create({
  baseURL: API_URL,
  timeout: 15000,
});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

let isRefreshing = false;
let failedQueue  = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((p) => (error ? p.reject(error) : p.resolve(token)));
  failedQueue = [];
};

let _navigate = null;
export const setNavigate = (fn) => { _navigate = fn; };

const softRedirectToLogin = () => {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('user');
  if (_navigate) {
    _navigate('/login');
  } else {
    window.location.href = '/login';
  }
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;

    if (error.response?.status === 503) {
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !original._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            original.headers.Authorization = `Bearer ${token}`;
            return api(original);
          })
          .catch((err) => Promise.reject(err));
      }

      original._retry  = true;
      isRefreshing     = true;

      const storedRefresh = localStorage.getItem('refreshToken');

      if (!storedRefresh) {
        isRefreshing = false;
        softRedirectToLogin();
        return Promise.reject(error);
      }

      try {
        const { data } = await axios.post(`${API_URL}/auth/refresh-token`, {
          refreshToken: storedRefresh,
        });
        const { accessToken, refreshToken: newRefresh } = data.data;
        localStorage.setItem('accessToken', accessToken);
        localStorage.setItem('refreshToken', newRefresh);
        api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
        processQueue(null, accessToken);
        original.headers.Authorization = `Bearer ${accessToken}`;
        return api(original);
      } catch {
        processQueue(null, null);
        softRedirectToLogin();
        return Promise.reject(error);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login:    (data) => api.post('/auth/login', data),
  logout:   () => api.post('/auth/logout'),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword:  (data) => api.post('/auth/reset-password', data),
};

export const userAPI = {
  getMe:          () => api.get('/users/me'),
  addAddress:     (data) => api.post('/users/me/addresses', data),
  removeAddress:  (id) => api.delete(`/users/me/addresses/${id}`),
  getAllUsers:     (params) => api.get('/users', { params }),
  getAvailableTechnicians: (city) => api.get('/users/technicians/available', { params: { city } }),
  toggleAvailability: (isAvailable) => api.patch('/users/technicians/availability', { isAvailable }),
  verifyTechnician:   (id) => api.patch(`/users/technicians/${id}/verify`),
};

export const categoryAPI = {
  getAll:  () => api.get('/categories'),
  getById: (id) => api.get(`/categories/${id}`),
  create:  (data) => api.post('/categories', data),
  update:  (id, data) => api.put(`/categories/${id}`, data),
  delete:  (id) => api.delete(`/categories/${id}`),
};

const withFormData = (data) =>
  data instanceof FormData
    ? { headers: { 'Content-Type': 'multipart/form-data' } }
    : {};

export const serviceAPI = {
  getAll:    (params) => api.get('/services', { params }),
  search:    (params) => api.get('/services/search', { params }),
  getBySlug: (slug) => api.get(`/services/${slug}`),
  create:    (data) => api.post('/services', data, withFormData(data)),
  update:    (id, data) => api.put(`/services/${id}`, data, withFormData(data)),
  delete:    (id) => api.delete(`/services/${id}`),
};

export const bookingAPI = {
  create:            (data) => api.post('/bookings', data),
  getAll:            (params) => api.get('/bookings', { params }),
  getById:           (id) => api.get(`/bookings/${id}`),
  cancel:            (id, reason) => api.patch(`/bookings/${id}/cancel`, { reason }),
  assign:            (id, technicianId) => api.patch(`/bookings/${id}/assign`, { technicianId }),
  updateStatus:      (id, status, note) => api.patch(`/bookings/${id}/status`, { status, note }),
  verifyOTP:         (id, otp) => api.post(`/bookings/${id}/verify-otp`, { otp }),
  getTechnicianJobs: (params) => api.get('/bookings/technician/jobs', { params }),
};

export const reviewAPI = {
  create:               (data) => api.post('/reviews', data),
  getTechnicianReviews: (techId, params) => api.get(`/reviews/technician/${techId}`, { params }),
  getServiceReviews:    (serviceId, params) => api.get(`/reviews/service/${serviceId}`, { params }),
};

export default api;
